import Immutable from "immutable";
import * as Rata from "js/common/utils/remote-data";

export const WEIGHT_BUDGET = 1000;

export const TabLoadStatus = Object.freeze({
  NEVER_LOADED: "never-loaded",
  LOADING: "loading",
  PARTIAL: "partial",
  LOADED: "loaded",
});

export const createEmptyEntry = ({loadStatus = TabLoadStatus.NEVER_LOADED} = {}) => Immutable.Map({
  idToData: Immutable.Map(),
  componentIdToLoadId: Immutable.Map(),
  uiState: Immutable.Map(),
  weightEstimate: 0,
  loadStatus,
  lastActiveAt: 0
});

export const getLoadStatus = (cache, tabId) => {
  const entry = cache.get(tabId);
  return entry ? entry.get("loadStatus") : TabLoadStatus.NEVER_LOADED;
};

const recomputeLoadStatus = entry => {
  const idToData = entry.get("idToData");
  if (idToData.isEmpty()) return entry.get("loadStatus");
  const stillLoading = idToData.some(record => Rata.isLoading(record.get("rata")));
  if (!stillLoading) return TabLoadStatus.LOADED;
  const anySettled = idToData.some(record => !Rata.isLoading(record.get("rata")));
  return anySettled ? TabLoadStatus.PARTIAL : TabLoadStatus.LOADING;
};

export const setComponentData = (cache, tabId, dataId, wrapper, weight) => {
  if (!cache.has(tabId)) return cache;
  return cache.update(tabId, entry => {
    const previous = entry.getIn(["idToData", dataId]);
    const previousWeight = previous ? previous.get("weight", 0) : 0;
    const updated = entry
        .setIn(["idToData", dataId], Immutable.Map({rata: wrapper, weight}))
        .update("weightEstimate", n => n - previousWeight + weight);
    return updated.set("loadStatus", recomputeLoadStatus(updated));
  });
};

export const setUiState = (cache, tabId, componentId, state) => {
  if (!cache.has(tabId)) return cache;
  return cache.setIn([tabId, "uiState", componentId], state);
};

export const markActive = (cache, tabId, now) => {
  if (!cache.has(tabId)) return cache;
  return cache.setIn([tabId, "lastActiveAt"], now);
};

export const evictTab = (cache, tabId) => cache.delete(tabId);

export const getActiveIdToData = (cache, activeTabId) => {
  const entry = cache.get(activeTabId);
  if (!entry) return Immutable.Map();
  return entry.get("idToData").map(record => record.get("rata"));
};

export const getCachedTabIds = cache => cache.keySeq().toList();

const totalWeight = cache =>
    cache.reduce((total, entry) => total + entry.get("weightEstimate"), 0);

const evictionCandidates = (cache, pinnedTabIds) => {
  const pinned = Immutable.Set(pinnedTabIds);
  return cache
      .filter((_entry, tabId) => !pinned.has(tabId))
      .sortBy(entry => entry.get("lastActiveAt"))
      .keySeq()
      .toList();
};

export const enforceBudget = (cache, {weightBudget, pinnedTabIds}) => {
  const currentWeight = totalWeight(cache);
  if (currentWeight <= weightBudget) return cache;
  const oldest = evictionCandidates(cache, pinnedTabIds).first();
  return oldest === undefined ? cache : cache.delete(oldest);
};
